import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getValidationErrorDetails } from '../utils/errorHandler';

/**
 * Holds server-side (422) validation errors so they render on the input that caused them.
 *
 * The API returns them as { success: false, data: { field: ["message"] } } — getValidationErrorDetails
 * normalizes that; nested keys arrive flattened (dba.phone -> dba_phone), so pass `fieldMap`
 * when the form field is named differently.
 *
 * @param {Object} fieldMap - server key -> form field name, e.g. { assignees: 'assignee_ids' }
 */
export const useServerValidationErrors = (fieldMap = {}) => {
  const [errors, setErrors] = useState({});
  const fieldMapRef = useRef(fieldMap);
  fieldMapRef.current = fieldMap;

  /** Shows the toast and puts each message on its field. Returns the mapped errors. */
  const applyApiError = useCallback((error, fallbackMessage) => {
    const { message, errors: apiErrors } = getValidationErrorDetails(error);
    const mapped = Object.fromEntries(
      Object.entries(apiErrors || {}).map(([key, msg]) => [
        fieldMapRef.current[key] || key,
        msg,
      ])
    );
    setErrors(mapped);
    toast.error(message || fallbackMessage || 'Failed to save. Please try again.');
    return mapped;
  }, []);

  /** Drop one field's error — call it when the user edits that field. */
  const clearError = useCallback((field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  }, []);

  const clearErrors = useCallback(() => setErrors({}), []);

  /** Spread onto a TextField: {...fieldProps('email')} */
  const fieldProps = useCallback(
    (field) => ({
      error: Boolean(errors[field]),
      helperText: errors[field] || undefined,
    }),
    [errors]
  );

  return { errors, setErrors, applyApiError, clearError, clearErrors, fieldProps };
};

export default useServerValidationErrors;
